/**
 * A one-shot message handed to the sign-in or sign-up screen that comes next.
 *
 * <h2>Two things use it, and the second is why the name is not narrower</h2>
 *
 * A session ending is the original: {@link onSessionEnded} leaves with a full page
 * load, so the person arrives at a sign-in form with no explanation of why they
 * are looking at it.
 *
 * <p>A refused social sign-in is the second, since 13 October 2026.
 * `OAuthCallbackPage` is reached from a provider's redirect and every outcome
 * leaves it, so the server's sentence — "Please use your work email address.
 * Personal Gmail accounts are not allowed." — has to reach the page they started
 * on. Both are the same shape: one sentence, written once, read by whichever
 * screen renders next, and shown in the alert that screen already has.
 *
 * <h2>Why this exists at all</h2>
 *
 * {@link onSessionEnded} leaves with `window.location.assign`, a full page load,
 * so nothing in memory survives — not component state, not the router's
 * `location.state`. Without somewhere to put it, the person arrives at a sign-in
 * form with no explanation of why they are looking at it.
 *
 * <h2>Why `sessionStorage` rather than the URL</h2>
 *
 * `?reason=session-expired` was the obvious alternative and is worse on three
 * counts. It survives bookmarking, sharing and reloading, so the message
 * reappears later on a perfectly healthy sign-in page. It is writable by anyone,
 * so a link could make this application display a banner of someone else's
 * choosing. And it is one more thing to strip before navigating onward.
 *
 * <p>`sessionStorage` has none of those: it survives the hard navigation, dies
 * with the tab, and cannot be forged into a link.
 *
 * <h2>Read once</h2>
 *
 * {@link takeSessionNotice} clears as it reads. A notice explains the navigation
 * that just happened; leaving it in place would explain the next one too, and the
 * person would see "your session has ended" after signing out on purpose, or
 * after a deliberate return to the sign-in page.
 */
const KEY = 'xt.session.notice';

/**
 * Records why somebody is being sent to a sign-in form. Silent on failure: a
 * private window or blocked site data may refuse `sessionStorage`, and losing the
 * sentence is not a reason to break the sign-out or the redirect it accompanies.
 */
export function setSessionNotice(message: string): void {
  try {
    window.sessionStorage.setItem(KEY, message);
  } catch {
    // Storage unavailable. The person still reaches sign-in, just without the reason.
  }
}

/** The pending notice, if any, removing it so it is shown exactly once. */
export function takeSessionNotice(): string | null {
  try {
    const message = window.sessionStorage.getItem(KEY);
    if (message !== null) {
      window.sessionStorage.removeItem(KEY);
    }
    return message;
  } catch {
    return null;
  }
}

/** Drops any pending notice. For tests, and for a sign-out the person chose. */
export function clearSessionNotice(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do: there is no notice to show either way.
  }
}
